
import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ArticleStatus } from "@/types/article-types";

interface PublishingTabSectionProps {
  status: ArticleStatus;
  setStatus: (value: ArticleStatus) => void;
  publishDate?: Date;
  setPublishDate: (date: Date | undefined) => void;
  calendarOpen: boolean;
  setCalendarOpen: (open: boolean) => void;
  formErrors: Record<string, string>;
}

const PublishingTabSection = ({
  status,
  setStatus,
  publishDate,
  setPublishDate,
  calendarOpen,
  setCalendarOpen,
  formErrors
}: PublishingTabSectionProps) => {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="status">Publishing Status</Label>
        <Select value={status} onValueChange={(value: ArticleStatus) => setStatus(value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {status === "scheduled" && (
        <div className="space-y-2">
          <Label>Publish Date</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !publishDate && "text-muted-foreground",
                  formErrors.publishDate && "border-red-500"
                )}
                type="button"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {publishDate ? format(publishDate, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={publishDate}
                onSelect={(date) => {
                  if (date) {
                    setPublishDate(date);
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {formErrors.publishDate && (
            <p className="text-red-500 text-sm">{formErrors.publishDate}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PublishingTabSection;
